import React, {
  useState,
} from 'react';

import {
  Flex,
} from 'web-styled';

import Link from '../Link';
import * as S from '../styled';

interface Item {
  id?: number
  link: string
  icon?: string
  name: string
  level?: number
  isFolder?: boolean
  children?: Item[]
  sectionActivePath?: string
  additionalPaths?: string[]
  inactivePaths?: string[]
  requiresPremium?: boolean
};

type Props = {
  isFolder?: boolean;
  badge?: string;
  children?: Item[];
  onClick?: { (e: any): void }
} & Item;

const getItemPath = ({
  id = 0,
  link,
  isFolder,
}: Pick<Props, 'id' | 'link' | 'isFolder'>) => {
  const basePath = isFolder ? `${link}_folder` : link;
  return `/${basePath}${id ? `/${id}` : ''}`;
};

const getDescendantPaths = (items: Item[] = [], parentLink?: string): string[] => (
  items.flatMap(item => {
    const itemLink = item.link || parentLink || '';
    const itemPath = getItemPath({
      id: item.id,
      link: itemLink,
      isFolder: item.isFolder,
    });

    return [
      itemPath,
      ...getDescendantPaths(item.children, itemLink),
    ];
  })
);

const SidebarItem = React.forwardRef<HTMLDivElement, Props>(({
  id = 0,
  link,
  icon,
  name,
  badge,
  isFolder,
  children,
  sectionActivePath,
  additionalPaths = [],
  inactivePaths = [],
  requiresPremium,
  onClick,
  ...rest
}: Props, ref) => {
  const level = rest.level || 0;
  const [isOpen, setOpen] = useState(false);

  const to = getItemPath({
    id,
    link,
    isFolder,
  });
  const sectionPath = link.split('/')[0];
  const activePaths = [
    ...(level === 0 ? [`/${sectionActivePath || sectionPath}`] : [to]),
    ...(level === 0 && isFolder ? [`/${link}`] : []),
    ...getDescendantPaths(children, link),
    ...additionalPaths.map(path => `/${path.replace(/^\//, '')}`),
  ];

  return (
    <Flex
      column
      width="100%"
      shrink="0"
      ref={ref}
      {...rest}
    >
      <Link
        to={to}
        icon={icon}
        name={name}
        level={level || 0}
        badge={badge}
        isOpen={isOpen}
        isFolder={isFolder}
        activePaths={activePaths}
        inactivePaths={inactivePaths.map(path => `/${path.replace(/^\//, '')}`)}
        matchSubpaths
        requiresPremium={requiresPremium}
        hasChild={children?.length > 0 && !requiresPremium}
        onClick={onClick}
        setOpen={setOpen}
      />

      {isOpen && (
        <S.Sub
          isOpen={isOpen}
          showLine={isFolder && level > 0}
          leftLine={`${level > 1 ? (level * 35) - 21 : 21}px`}
        >
          {children.map(item => (
            <SidebarItem
              key={item.id || item.link}
              link={item.link || link}
              level={level ? level + 1 : 1}
              icon={icon}
              {...item}
            />
          ))}
        </S.Sub>
      )}
    </Flex>
  );
});

export default SidebarItem;
